import { useMemo } from "react";
import { useQuery } from "react-query";

import { Collection, getMainTabs, Video } from "@/api";
import { TEMPLATE_COMMUNITY } from "@/const";
import { filterUnsupportedItems } from "@/utils";

const key = "maintabs";

const isValidCommunityTab = (tab: Collection) => {
  const communityId = tab.Properties?.CommunityID;
  return tab.TemplateId === TEMPLATE_COMMUNITY && !!communityId;
};

export const useMainTabs = (props?: { refetchOnMount: "always" | boolean }) => {
  const { refetchOnMount } = props ?? {};
  const query = useQuery(
    [key],
    () => {
      return getMainTabs();
    },
    {refetchOnMount},
  );

  const collectionsById = useMemo(() => {
    if (!query?.data) {
      return undefined;
    }

    const collectionsById: Record<number, Collection> = {};
    for (const collection of query.data.Collections) {
      collectionsById[collection.TabId] = {
        ...collection,
        Items: filterUnsupportedItems(collection.Items),
      };
    }
    return collectionsById;
  }, [query.data]);

  const videosById = useMemo(() => {
    if (!query?.data) {
      return undefined;
    }
    const v_by_id: Record<number, Video> = {};
    for (const video of query.data.Videos) {
      v_by_id[video.VideoId] = video;
    }
    return v_by_id;
  }, [query.data]);

  const mainTabs = useMemo(() => {
    return query.data?.Tabs.sort();
  }, [query.data]);

  const sortedClickableMainTabs = useMemo(() => {
    const sorted = mainTabs?.sort((a, b) => a.Position - b.Position);
    return sorted?.filter((t) => t.Items.length > 0);
  }, [mainTabs]);

  const communityTab = useMemo(
    () => mainTabs?.find((t) => isValidCommunityTab(t)),
    [mainTabs],
  );

  return {
    ...query,
    collectionsById,
    mainTabs,
    videosById,
    sortedClickableMainTabs,
    communityTab,
  };
};
